import React, { useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Box,
  Chip,
  CircularProgress,
  alpha,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  ListSubheader,
  Stack
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  FinancialDocument,
  MetricDocument,
  MetricAverages
} from '@features/analysis/types';

interface DataSourceSelectorProps {
  dataSources: string[];
  onDataSourceChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  financialDocuments: FinancialDocument[];
  selectedDocuments: string[];
  onDocumentChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  loadingDocuments: boolean;
  hotelMetrics: MetricDocument[];
  selectedMetrics: string[];
  onMetricChange: (event: React.ChangeEvent<HTMLInputElement> | { target: { name: string; checked: boolean; value?: string[] } }) => void;
  loadingMetrics: boolean;
  averageMetrics: MetricAverages;
}

const DataSourceSelector: React.FC<DataSourceSelectorProps> = ({
  dataSources,
  onDataSourceChange,
  financialDocuments,
  selectedDocuments,
  onDocumentChange,
  loadingDocuments,
  hotelMetrics,
  selectedMetrics,
  onMetricChange,
  loadingMetrics,
  averageMetrics
}) => {
  const theme = useTheme();
  const [expandedFinancials, setExpandedFinancials] = useState(false);
  const [expandedMetrics, setExpandedMetrics] = useState(false);

  const dataSourceConfig = [
    {
      id: 'market',
      icon: SearchIcon,
      title: 'Market Research',
      description: 'Industry trends and competitive analysis',
      color: theme.palette.primary.main,
      count: null
    },
    {
      id: 'financial',
      icon: DescriptionIcon,
      title: 'Financial Documents',
      description: 'P&L statements, balance sheets, and reports',
      color: theme.palette.info.main,
      count: selectedDocuments.length,
      totalCount: financialDocuments.length
    },
    {
      id: 'metrics',
      icon: TrendingUpIcon,
      title: 'Performance Metrics',
      description: 'ADR, RevPAR, and occupancy data',
      color: theme.palette.success.main,
      count: selectedMetrics.length,
      totalCount: hotelMetrics.length
    }
  ];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
        <SearchIcon sx={{ color: theme.palette.primary.main }} />
        Data Sources
      </Typography>

      <Grid container spacing={2}>
        {dataSourceConfig.map((source) => (
          <Grid item xs={12} sm={6} md={4} key={source.id}>
            <Card
              sx={{
                height: '100%',
                border: `1px solid ${dataSources.includes(source.id) ? source.color : alpha(theme.palette.divider, 0.3)}`,
                borderRadius: 1.5,
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                bgcolor: dataSources.includes(source.id) ? alpha(source.color, 0.02) : 'transparent',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: theme.shadows[2],
                  borderColor: source.color
                }
              }}
              onClick={() => onDataSourceChange({ target: { name: source.id, checked: !dataSources.includes(source.id) } } as any)}
            >
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <source.icon sx={{ color: source.color, fontSize: 24 }} />
                  <IconButton
                    size="small"
                    sx={{ p: 0.5 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDataSourceChange({ target: { name: source.id, checked: !dataSources.includes(source.id) } } as any);
                    }}
                  >
                    {dataSources.includes(source.id) ?
                      <CheckCircleIcon sx={{ color: source.color, fontSize: 20 }} /> :
                      <RadioButtonUncheckedIcon sx={{ color: theme.palette.text.disabled, fontSize: 20 }} />
                    }
                  </IconButton>
                </Box>

                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.95rem' }}>
                  {source.title}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.8rem', lineHeight: 1.3 }}>
                  {source.description}
                </Typography>

                {source.count !== null && (
                  <Box sx={{ mt: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        label={`${source.count} selected`}
                        color={dataSources.includes(source.id) ? "primary" : "default"}
                        variant={dataSources.includes(source.id) ? "filled" : "outlined"}
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                      {source.totalCount && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          of {source.totalCount}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}

        {/* Financial Documents Expansion */}
        {dataSources.includes('financial') && (
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer'
                }}
                onClick={() => setExpandedFinancials(!expandedFinancials)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FileOpenIcon sx={{ color: theme.palette.info.main }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Select Financial Documents
                  </Typography>
                  <Chip
                    size="small"
                    label={`${selectedDocuments.length} of ${financialDocuments.length} selected`}
                    color="info"
                    variant="outlined"
                  />
                </Box>
                <IconButton size="small">
                  {expandedFinancials ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              {expandedFinancials && (
                <CardContent sx={{ p: 0 }}>
                  {loadingDocuments ? (
                    <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : financialDocuments.length > 0 ? (
                    <List sx={{ py: 0 }}>
                      {financialDocuments.map((doc) => (
                        <ListItem
                          key={doc.id}
                          sx={{
                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            '&:last-child': { borderBottom: 'none' },
                            '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.05) }
                          }}
                        >
                          <ListItemIcon>
                            <Checkbox
                              edge="start"
                              checked={selectedDocuments.includes(doc.id)}
                              onChange={onDocumentChange}
                              name={doc.id}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={doc.original_filename}
                            secondary={`${doc.document_type || doc.category || 'Document'} • ${new Date(doc.created_at).toLocaleDateString()}`}
                            primaryTypographyProps={{ fontWeight: selectedDocuments.includes(doc.id) ? 600 : 400 }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No financial documents available. Upload documents to include them in the analysis.
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              )}
            </Card>
          </Grid>
        )}

        {/* Metrics Expansion */}
        {dataSources.includes('metrics') && (
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.success.main, 0.05),
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer'
                }}
                onClick={() => setExpandedMetrics(!expandedMetrics)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon sx={{ color: theme.palette.success.main }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Select Performance Metrics
                  </Typography>
                  <Chip
                    size="small"
                    label={`${selectedMetrics.length} metrics selected`}
                    color="success"
                    variant="outlined"
                  />
                </Box>
                <IconButton size="small">
                  {expandedMetrics ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              {expandedMetrics && (
                <>
                  {/* Averages Summary */}
                  <Box sx={{ p: 2, bgcolor: alpha(theme.palette.background.paper, 0.5), borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">Avg ADR</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                            ${averageMetrics.adr.avg.toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">Avg RevPAR</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.info.main }}>
                            ${averageMetrics.revpar.avg.toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">Avg Occupancy</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.warning.main }}>
                            {averageMetrics.occupancy.avg.toFixed(1)}%
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>

                  <CardContent sx={{ p: 0 }}>
                    {loadingMetrics ? (
                      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : hotelMetrics.length > 0 ? (
                      <List sx={{ py: 0 }}>
                        {['adr', 'revpar', 'occupancy'].map(metricType => {
                          const metricsOfType = hotelMetrics.filter(m => m.type.toLowerCase() === metricType);
                          if (metricsOfType.length === 0) return null;

                          const allSelected = metricsOfType.every(m => selectedMetrics.includes(m.id));
                          const someSelected = metricsOfType.some(m => selectedMetrics.includes(m.id));

                          // Get unique years for this metric type
                          const uniqueYears = [...new Set(metricsOfType.map(m => m.year))];
                          const selectedCount = metricsOfType.filter(m => selectedMetrics.includes(m.id)).length;

                          const handleToggleAll = () => {
                            const metricIds = metricsOfType.map(m => m.id);
                            if (allSelected) {
                              // Deselect all metrics of this type
                              const newSelected = selectedMetrics.filter(id => !metricIds.includes(id));
                              onMetricChange({ target: { name: 'bulkUpdate', checked: false, value: newSelected } } as any);
                            } else {
                              // Select all metrics of this type
                              const newSelected = [...new Set([...selectedMetrics, ...metricIds])];
                              onMetricChange({ target: { name: 'bulkUpdate', checked: true, value: newSelected } } as any);
                            }
                          };

                          return (
                            <Box key={metricType}>
                              <ListItem
                                sx={{
                                  bgcolor: alpha(theme.palette.background.default, 0.3),
                                  borderBottom: `1px solid ${theme.palette.divider}`,
                                  cursor: 'pointer',
                                  '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.1) }
                                }}
                                onClick={handleToggleAll}
                              >
                                <ListItemIcon>
                                  <Checkbox
                                    edge="start"
                                    checked={allSelected}
                                    indeterminate={someSelected && !allSelected}
                                    sx={{
                                      color: metricType === 'adr' ? theme.palette.success.main :
                                             metricType === 'revpar' ? theme.palette.info.main :
                                             theme.palette.warning.main
                                    }}
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        {metricType === 'adr' ? 'Average Daily Rate (ADR)' :
                                         metricType === 'revpar' ? 'Revenue Per Available Room (RevPAR)' :
                                         'Occupancy Rate'}
                                      </Typography>
                                      <Chip
                                        size="small"
                                        label={`${selectedCount}/${uniqueYears.length} years`}
                                        color={allSelected ? "success" : someSelected ? "warning" : "default"}
                                        variant="outlined"
                                      />
                                    </Box>
                                  }
                                  secondary={
                                    <Typography variant="body2" color="text.secondary">
                                      {uniqueYears.length} years of data from {Math.min(...uniqueYears)} to {Math.max(...uniqueYears)}
                                      {someSelected && ` • ${selectedCount} data points selected`}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                            </Box>
                          );
                        })}
                      </List>
                    ) : (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          No performance metrics available. Add metrics in the hotel details section.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </>
              )}
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default DataSourceSelector;